/**
 * Single source of truth for the SalesTrak analytics taxonomy, shared across
 * the workspace. `AnalyticsContext` and `AnalyticsScreenName` are TS string
 * enums — their runtime values ARE the wire strings sent to the analytics API,
 * so a typo'd context or screen name can't silently fragment the dataset.
 *
 * The event catalog stays a literal map (not an enum) on purpose: it powers the
 * discriminated `TrackEvent` union below, which narrows `event` to a context's
 * allowed events at every call site. Add new contexts/events/screens here first.
 */

export const AnalyticsContext = {
  Announcement: "announcement",
  Auth: "auth",
  Automation: "automation",
  Dashboard: "dashboard",
  FollowUp: "follow_up",
  Inbox: "inbox",
  Integration: "integration",
  Issue: "issue",
  Kanban: "kanban",
  Map: "map",
  Member: "member",
  Navigation: "navigation",
  Note: "note",
  OrgChart: "org_chart",
  Search: "search",
  Settings: "settings",
  Sharing: "sharing",
  Team: "team",
  View: "view",
  WidgetView: "widget_view",
  Workspace: "workspace",
} as const;
export type AnalyticsContext = (typeof AnalyticsContext)[keyof typeof AnalyticsContext];

/**
 * Named event constants — one per `event` string, so call sites reference
 * `IssueEvent.ChangeGrouping` instead of a bare `"change_grouping"`. These are
 * `as const` objects (not TS `enum`s) on purpose: their member types ARE the
 * string-literal types, so they stay assignable to the `TrackEvent` union built
 * from `ANALYTICS_EVENTS` below — a string `enum` member is nominal and would
 * not. `ANALYTICS_EVENTS` references these members, so the catalog and the
 * constants can never drift.
 */
export const IssueEvent = {
  View: "view",
  Edit: "edit",
  CreateStart: "create_start",
  CreateFinish: "create_finish",
  UpdateAge: "update_age",
  ChangeGrouping: "change_grouping",
  ChangeSorting: "change_sorting",
  Search: "search",
  ApplyFilter: "apply_filter",
  ToggleOnlyMe: "toggle_only_me",
  SwitchTab: "switch_tab",
  BulkChangeStage: "bulk_change_stage",
  BulkUpdateAssignees: "bulk_update_assignees",
  BulkArchive: "bulk_archive",
  BulkUnarchive: "bulk_unarchive",
  BulkDelete: "bulk_delete",
  BulkUpdateTeams: "bulk_update_teams",
  BulkMoveWorkflow: "bulk_move_workflow",
  BulkUpdateSource: "bulk_update_source",
  BulkUpdateCustomField: "bulk_update_custom_field",
  // The filter panel's saved-filter list: Save (create), a row press
  // (apply), Update (re-snapshot), rename and delete.
  SaveFilter: "save_filter",
  ApplySavedFilter: "apply_saved_filter",
  UpdateSavedFilter: "update_saved_filter",
  RenameSavedFilter: "rename_saved_filter",
  DeleteSavedFilter: "delete_saved_filter",
  // The filter panel's Simple/Advanced tabs; `data.mode`.
  SwitchFilterMode: "switch_filter_mode",
  // The filter chip or the F hotkey opens the panel; `data.via`.
  OpenFilterPanel: "open_filter_panel",
  // The chip ×, the panel footer or ⌥⇧F clears every filter; `data.via`.
  ClearFilters: "clear_filters",
  // The quick-filter menu's "Filter by anything" value search.
  QuickFilterSearch: "quick_filter_search",
  // The detail view header: tab press, tab drag reorder, and the
  // sidebar/fullscreen size toggle.
  SwitchDetailTab: "switch_detail_tab",
  ReorderDetailTabs: "reorder_detail_tabs",
  ChangeDetailSize: "change_detail_size",
  // The summary's "Add fields" drawer or dialog confirm; `data.count`.
  AddFields: "add_fields",
  // An address cell's "Open on map" action.
  OpenAddressOnMap: "open_address_on_map",
  // The mention/assign access prompt's grant confirm.
  GrantAccess: "grant_access",
} as const;

// Automation lifecycle. `Create` fires once an automation is actually
// persisted (scratch, duplicate, or from a Browse template). Its `data`
// carries `source` ("scratch" | "duplicate" | "template"), `template` (the
// template key, or null), `scope` ("workspace" | "workflow") and `entry`
// (the door the builder opened from).
export const AutomationEvent = {
  Create: "create",
  // "Go live" on the manage list, the workflow popover or the editor.
  GoLive: "go_live",
  // The on/off toggle or the editor's Turn off, to inactive.
  TurnOff: "turn_off",
  // The editor's or the manage list's Delete.
  Delete: "delete",
  // The editor's "Create version".
  CreateVersion: "create_version",
  // The runs dialog opens from the issue menu, a touch marker or a toast.
  OpenRuns: "open_runs",
  // The manage list's search box (one per committed query).
  Search: "search",
} as const;

export const KanbanEvent = {
  Rename: "rename",
  Archive: "archive",
  Unarchive: "unarchive",
  Delete: "delete",
  ChangeStage: "change_stage",
  ChangeType: "change_type",
  Favorite: "favorite",
  Unfavorite: "unfavorite",
  MoveWorkflow: "move_workflow",
  /** @deprecated no emitter; kept because stored rows carry the key */
  UpdateOwners: "update_owners",
  UpdateAssignees: "update_assignees",
  DragMoveStage: "drag_move_stage",
  DragReorder: "drag_reorder",
  DragReassign: "drag_reassign",
  // The delete toast's Undo (or ⌘Z) brings a deleted project back.
  Restore: "restore",
  // The project menu's "Copy link" and "Open in new tab".
  CopyLink: "copy_link",
  OpenInNewTab: "open_in_new_tab",
} as const;

export const TeamEvent = {
  Open: "open",
  Leave: "leave",
  Search: "search",
  // The team breadcrumb menu's "Copy link".
  CopyLink: "copy_link",
  // The team masthead's @handle chip; `data.via`.
  CopyHandle: "copy_handle",
} as const;

export const WidgetViewEvent = {
  AddWidget: "add_widget",
  ConfigureWidget: "configure_widget",
  DeleteWidget: "delete_widget",
  DuplicateWidget: "duplicate_widget",
  ReplaceWidget: "replace_widget",
  Rearrange: "rearrange",
  FilterDate: "filter_date",
  /** @deprecated no emitter; kept because stored rows carry the key */
  FilterMembers: "filter_members",
  ToggleOnlyMe: "toggle_only_me",
  // Ownership model: the personal fork of a shared view. Personalize fires
  // when a viewer's first edit implicitly creates their fork;
  // ResetPersonalLayout deletes it (back to the live shared board);
  // SaveAsNewTab routes staged edits into a brand-new saved dashboard tab…
  Personalize: "personalize",
  ResetPersonalLayout: "reset_personal_layout",
  SaveAsNewTab: "save_as_new_tab",
  // …the writer's save control for staged shared edits…
  SaveForTeam: "save_for_team",
  DiscardStagedChanges: "discard_staged_changes",
  // …and a fork-holding writer's temporary jump to the shared board
  // ("Edit team layout" ↔ the banner's Done).
  EditTeamLayout: "edit_team_layout",
  ExitTeamLayout: "exit_team_layout",
  // The toolbar's board-level refresh (re-runs every widget's queries).
  // Only a press sends it (`auto: false`); auto-refresh ticks send nothing.
  RefreshBoard: "refresh_board",
  // The display-options Auto-layout switch (dense grid flow keeps the board
  // packed); payload carries `enabled`.
  ToggleAutoLayout: "toggle_auto_layout",
  // The display-options Auto-refresh switch (the board re-fetches on a fixed
  // cadence while the tab is visible); payload carries `enabled`.
  ToggleAutoRefresh: "toggle_auto_refresh",
  // The empty board's template chooser: a template card or "Start from
  // scratch"; `data.template` is the template key or "scratch".
  ChooseStartingPoint: "choose_starting_point",
  // A widget's drilldown, chart or maximize opens its detail dialog.
  OpenWidgetDetail: "open_widget_detail",
  // A widget header's filter popover saves its filter.
  FilterWidget: "filter_widget",
  // The detail dialog's per-widget refresh.
  RefreshWidget: "refresh_widget",
  // A preset widget's "Convert to custom".
  ConvertToCustom: "convert_to_custom",
  // The detail dialog's chart/table toggle; `data.mode`.
  ToggleDetailView: "toggle_detail_view",
  // The managed board's greeting show/hide.
  ToggleGreeting: "toggle_greeting",
  // The grid history hotkeys (⌘Z / ⌘⇧Z), one per burst.
  Undo: "undo",
  Redo: "redo",
  /** @deprecated no emitter; kept because stored rows carry the key */
  SwitchViewLens: "switch_view_lens",
  /** @deprecated no emitter; kept because stored rows carry the key */
  SaveAsPersonal: "save_as_personal",
  /** @deprecated no emitter; kept because stored rows carry the key */
  EnterEditMode: "enter_edit_mode",
  /** @deprecated no emitter; kept because stored rows carry the key */
  ExitEditMode: "exit_edit_mode",
  /** @deprecated no emitter; kept because stored rows carry the key */
  ApplyTemplate: "apply_template",
  /** @deprecated no emitter; kept because stored rows carry the key */
  ClearBoard: "clear_board",
} as const;

export const AuthEvent = {
  SignIn: "sign_in",
  SignUp: "sign_up",
  SignOut: "sign_out",
  // The forgot-password form's submit, and the reset-password form's submit.
  PasswordResetRequest: "password_reset_request",
  PasswordReset: "password_reset",
} as const;

export const NavigationEvent = {
  ScreenView: "screen_view",
  // A module tile press (rail, strip, More) or a G-chord; `data.module`.
  ModuleOpen: "module_open",
  // Any app-level dialog opens through its emitter; `data.dialog`.
  OpenDialog: "open_dialog",
  // A page-level chord fires through the shared dispatcher; `data.hotkey`.
  UseHotkey: "use_hotkey",
  // The route error boundary shows its screen, and its "Copy details".
  ErrorScreen: "error_screen",
  CopyErrorDetails: "copy_error_details",
  // The Customize navigation dialog: tile show/hide, reorder, compact mode.
  CustomizeNav: "customize_nav",
  // The sidebar collapse button or its hotkey.
  ToggleSidebar: "toggle_sidebar",
  // A link in the sidebar's Apps section.
  OpenExternalApp: "open_external_app",
  // The keyboard shortcuts dialog's search box.
  SearchShortcuts: "search_shortcuts",
  // The mobile-app notice shows (the download-rate denominator), then its
  // "Get the mobile app" or "Continue to website"; `data.platform`.
  MobileAppNoticeShown: "mobile_app_notice_shown",
  MobileAppDownload: "mobile_app_download",
  MobileAppNoticeDismiss: "mobile_app_notice_dismiss",
} as const;

export const NoteEvent = {
  Create: "create",
  Update: "update",
  Delete: "delete",
  React: "react",
} as const;

export const SearchEvent = {
  Command: "command",
  SelectResult: "select_result",
  // ⌘K, the sidebar search button or the search bar opens search; `data.via`.
  Open: "open",
  // One per committed query in global search; never the term.
  Search: "search",
  // A kind chip toggles; `data.kind`, `data.enabled`.
  ToggleKind: "toggle_kind",
  // A result section's "Load more".
  LoadMore: "load_more",
} as const;

export const SettingsEvent = {
  Create: "create",
  Update: "update",
  Delete: "delete",
  Archive: "archive",
  Unarchive: "unarchive",
  Revoke: "revoke",
  Reorder: "reorder",
  SetDefault: "set_default",
  Duplicate: "duplicate",
  AddMembers: "add_members",
  RemoveMembers: "remove_members",
  UpdateMembership: "update_membership",
  UpdateRoles: "update_roles",
  UpdateTeams: "update_teams",
  ResendInvite: "resend_invite",
  CancelInvite: "cancel_invite",
  Invite: "invite",
  // The settings sidebar's search box, and a press on one of its results.
  Search: "search",
  SelectSearchResult: "select_search_result",
  // Trash: a row's Restore, a row's Delete forever, and "Empty trash".
  Restore: "restore",
  DeletePermanently: "delete_permanently",
  ClearAll: "clear_all",
} as const;

export const WorkspaceEvent = {
  Switch: "switch",
  // The invite gate, the picker row or the email link: Join or Decline.
  AcceptInvite: "accept_invite",
  DeclineInvite: "decline_invite",
  // The invite gate screen shows (the accept-rate denominator).
  InviteGateShown: "invite_gate_shown",
  // The workspace-create form's submit.
  Create: "create",
  // The Leave workspace dialog's confirm.
  Leave: "leave",
  // The danger zone's Delete workspace, and the pending screen's Restore.
  ScheduleDeletion: "schedule_deletion",
  CancelDeletion: "cancel_deletion",
} as const;

// The Dashboards module: the directory, its row menu, the sidebar module
// menu and the workspace dashboard masthead.
export const DashboardEvent = {
  // The New dashboard dialog's Create; `data.via` names the door.
  Create: "create",
  // A dashboard renders for a view id (once per view, beside the visit).
  Open: "open",
  Duplicate: "duplicate",
  Delete: "delete",
  Rename: "rename",
  // The star, wherever it shows.
  Favorite: "favorite",
  Unfavorite: "unfavorite",
  /** @deprecated The share dialog sends `sharing.*` with `object: "dashboard"`. */
  Share: "share",
  // The directory's search box (one per committed query).
  Search: "search",
} as const;

// Saved views on the board and list (the dashboard save family lives in
// WidgetViewEvent). Every event carries `type`, `scope` and `builtIn`.
export const ViewEvent = {
  // "+ View", a tab's Duplicate, or "Save as new tab"; `data.via`.
  Create: "create",
  // The Save button or ⌘Enter; `data.keys` names the changed settings.
  Save: "save",
  // The Discard control drops the staged changes.
  DiscardChanges: "discard_changes",
  Delete: "delete",
  // A tab menu's or the panel's Pin / Unpin.
  Pin: "pin",
  Unpin: "unpin",
  // A tab menu's or the panel's "Set as default".
  SetDefault: "set_default",
  // The Customize view panel opens; `data.page`, `data.via`.
  OpenPanel: "open_panel",
  Rename: "rename",
  CopyLink: "copy_link",
  // A Display / More options toggle or a field show/hide; `data.key`.
  ChangeOption: "change_option",
  // A drag in the tab strip or the More list.
  Reorder: "reorder",
  // The dashboard's workflow set in the panel.
  UpdateWorkflows: "update_workflows",
} as const;

// The Follow up lifecycle of a record: start, end (and its Undo), and a
// cycle override or its reset to the inherited default.
export const FollowUpEvent = {
  Start: "start",
  End: "end",
  UndoEnd: "undo_end",
  ChangeCycle: "change_cycle",
  ResetCycle: "reset_cycle",
} as const;

// The shared sharing dialog (projects, workflows, dashboards); `data.object`.
export const SharingEvent = {
  Open: "open",
  SetMemberAccess: "set_member_access",
  SetTeamAccess: "set_team_access",
  Unshare: "unshare",
  SetVisibility: "set_visibility",
  SetPublicAccess: "set_public_access",
  // The dialog's "Set all" access menu and "Remove all".
  SetAll: "set_all",
  RemoveAll: "remove_all",
  // The dialog's Link row "Copy link" button.
  CopyLink: "copy_link",
} as const;

// The member profile island and the members directory.
export const MemberEvent = {
  // The profile opens; `data.via` names the surface.
  OpenProfile: "open_profile",
  // The "Reports to" picker on the profile, table or roster.
  SetReportsTo: "set_reports_to",
  // The profile header's job-title picker.
  ChangeTitle: "change_title",
  // The profile's Overview / Activity / Org Chart tabs.
  SwitchProfileTab: "switch_profile_tab",
  // The directory or team roster search (one per committed query).
  Search: "search",
  // A quick filter chip; `data.facet`.
  ApplyFilter: "apply_filter",
  // The Group chip and the Sort chip.
  ChangeGrouping: "change_grouping",
  ChangeSorting: "change_sorting",
  // The Access column popover: grant, change level, revoke.
  UpdateAccess: "update_access",
  // A contact row's email, phone or copy action.
  Contact: "contact",
} as const;

export const OrgChartEvent = {
  // The profile Org Chart tab's "Explore org chart".
  ExploreFromProfile: "explore_from_profile",
  // The chart's jump search selects a member.
  JumpToMember: "jump_to_member",
  // The chart's view options (show title / show role).
  ToggleViewOption: "toggle_view_option",
} as const;

// The map screen. Never carries coordinates or addresses.
export const MapEvent = {
  // A pin press, or a click that scouts a new pin; `data.kind`.
  OpenPin: "open_pin",
  // The infobox's Directions.
  GetDirections: "get_directions",
  // The display-options panel (base map, layers, facts); `data.setting`.
  ChangeDisplayOption: "change_display_option",
  // The display-options card section; `data.option`.
  ChangeCardOption: "change_card_option",
  // The locate button, C key, or the blocked-location toast's retry.
  Locate: "locate",
  // The infobox property-details popover, and a fact's copy.
  OpenPropertyDetails: "open_property_details",
  CopyFact: "copy_fact",
} as const;

export const InboxEvent = {
  // A notification row press or its Enter.
  OpenNotification: "open_notification",
  // A row's clear (E) and un-clear, and its read toggle (U).
  Clear: "clear",
  Unclear: "unclear",
  ToggleRead: "toggle_read",
  // The header's "Clear all" (⇧E).
  ClearAll: "clear_all",
  // The kind filter (⇧1–3), the All/Cleared tabs, and the ordering menu.
  ApplyFilter: "apply_filter",
  SwitchTab: "switch_tab",
  ChangeOrdering: "change_ordering",
} as const;

// The App Center (integrations dialog); `data.provider` is the slug.
export const IntegrationEvent = {
  ConnectStart: "connect_start",
  ConnectFinish: "connect_finish",
  Disconnect: "disconnect",
} as const;

// The What's new channel: the dialog, the release toast, and a CTA.
export const AnnouncementEvent = {
  Open: "open",
  Cta: "cta",
  DismissToast: "dismiss_toast",
} as const;

/**
 * Named constants for the `data` payload VALUES that recur across call sites,
 * so the dataset can't fragment on a typo'd `"borad"` or `"set-primary"`.
 * (`data` itself stays an open `Record`; these just standardize the values we
 * reuse.) `AnalyticsScope` mirrors `IssueScope["kind"]`.
 */
export const AnalyticsView = {
  Board: "board",
  List: "list",
  Map: "map",
  Member: "member",
  WidgetView: "widget_view",
} as const;

export const AnalyticsScope = {
  Workflow: "workflow",
  // A workflow SET — the scope a workspace dashboard reads from.
  Workflows: "workflows",
  Team: "team",
  Member: "member",
  // A fixed set of linked issues (an account's or contact's projects).
  Issues: "issues",
  // The projects shared with the viewer (Home's "Shared with me").
  Shared: "shared",
} as const;

export const BulkOperation = {
  Add: "add",
  Remove: "remove",
  SetPrimary: "set_primary",
} as const;

export const BulkMode = {
  Where: "where",
  Ids: "ids",
} as const;

export type WidgetViewEvent = (typeof WidgetViewEvent)[keyof typeof WidgetViewEvent];
export type AnalyticsView = (typeof AnalyticsView)[keyof typeof AnalyticsView];
export type AnalyticsScope = (typeof AnalyticsScope)[keyof typeof AnalyticsScope];
export type BulkOperation = (typeof BulkOperation)[keyof typeof BulkOperation];
export type BulkMode = (typeof BulkMode)[keyof typeof BulkMode];

/** Every member of one event-constant object, typed as its literal union. */
function eventsOf<T extends Record<string, string>>(events: T): readonly T[keyof T][] {
  return Object.values(events) as T[keyof T][];
}

/**
 * The catalog: each context's allowed events. Each entry lists every member of
 * the context's constant object, deprecated keys included (stored rows carry
 * them), so a key added to a constant object is in the catalog at once.
 */
export const ANALYTICS_EVENTS = {
  [AnalyticsContext.Announcement]: eventsOf(AnnouncementEvent),
  [AnalyticsContext.Auth]: eventsOf(AuthEvent),
  [AnalyticsContext.Automation]: eventsOf(AutomationEvent),
  [AnalyticsContext.Dashboard]: eventsOf(DashboardEvent),
  [AnalyticsContext.FollowUp]: eventsOf(FollowUpEvent),
  [AnalyticsContext.Inbox]: eventsOf(InboxEvent),
  [AnalyticsContext.Integration]: eventsOf(IntegrationEvent),
  // `view`/`edit`/`create_*`/`update_age` are per-issue; the rest are view-level
  // interactions shared by the board, list, and member views (grouping,
  // sorting, search, filtering, Only-Me, board/list/dashboard tab switching,
  // bulk ops, saved filters, the detail view shell).
  [AnalyticsContext.Issue]: eventsOf(IssueEvent),
  [AnalyticsContext.Kanban]: eventsOf(KanbanEvent),
  [AnalyticsContext.Map]: eventsOf(MapEvent),
  [AnalyticsContext.Member]: eventsOf(MemberEvent),
  [AnalyticsContext.Navigation]: eventsOf(NavigationEvent),
  [AnalyticsContext.Note]: eventsOf(NoteEvent),
  [AnalyticsContext.OrgChart]: eventsOf(OrgChartEvent),
  [AnalyticsContext.Search]: eventsOf(SearchEvent),
  [AnalyticsContext.Settings]: eventsOf(SettingsEvent),
  [AnalyticsContext.Sharing]: eventsOf(SharingEvent),
  // Teams-list page: opening a team from the list, leaving a team, searching.
  [AnalyticsContext.Team]: eventsOf(TeamEvent),
  [AnalyticsContext.View]: eventsOf(ViewEvent),
  // The `WidgetView` host (my-work, workflow/team Dashboard tabs): widget
  // lifecycle + the view's date/member/Only-Me filter toolbar.
  [AnalyticsContext.WidgetView]: eventsOf(WidgetViewEvent),
  [AnalyticsContext.Workspace]: eventsOf(WorkspaceEvent),
} as const satisfies Record<AnalyticsContext, readonly string[]>;

type AnalyticsCatalog = typeof ANALYTICS_EVENTS;

/**
 * Discriminated on `context`, so `event` narrows to that context's events at
 * every call site.
 */
export type TrackEvent = {
  [C in AnalyticsContext]: {
    context: C;
    event: AnalyticsCatalog[C][number];
    label?: string;
    data?: Record<string, unknown>;
  };
}[AnalyticsContext];

/**
 * Migration-window continuity for the taxonomy rename (card→widget event
 * names, `surface`→`view` payload key). The NEW names stay canonical; these
 * maps are the single source of truth for the legacy wire names, consumed by
 * the client's central emit path (`AnalyticsClient.track` →
 * `expandLegacyAliases`) so downstream dashboards keyed on the old names keep
 * matching events until they migrate. Delete this whole block (and the
 * `expandLegacyAliases` call in the client) once downstream consumers have
 * moved to the canonical names.
 */

/** Renamed widget lifecycle events → their retired `*_card` wire names. */
export const LEGACY_EVENT_ALIASES = {
  [WidgetViewEvent.AddWidget]: "add_card",
  [WidgetViewEvent.ConfigureWidget]: "configure_card",
  [WidgetViewEvent.DeleteWidget]: "delete_card",
  [WidgetViewEvent.DuplicateWidget]: "duplicate_card",
} as const satisfies Partial<Record<WidgetViewEvent, string>>;

/** Renamed `data` payload keys → their retired twins, dual-written on every event. */
export const LEGACY_DATA_KEY_ALIASES = {
  view: "surface",
} as const satisfies Record<string, string>;

/**
 * Marker field stamped onto mirrored legacy-named events so downstream can
 * split (or drop) the duplicates without double counting.
 */
export const LEGACY_ALIAS_MARKER = "deprecated_alias";

export type LegacyEventAlias = (typeof LEGACY_EVENT_ALIASES)[keyof typeof LEGACY_EVENT_ALIASES];

/**
 * A mirrored event emitted under a retired event name. Same shape as
 * `TrackEvent`, but its `event` is a legacy wire name outside the canonical
 * catalog and its `data` carries `deprecated_alias: true`.
 */
export type LegacyAliasEvent = {
  context: typeof AnalyticsContext.WidgetView;
  event: LegacyEventAlias;
  label?: string;
  data?: Record<string, unknown>;
};

/** Dual-write renamed payload keys: `view` also goes out as legacy `surface`. */
function withLegacyDataKeys(data: Record<string, unknown>): Record<string, unknown> {
  let out = data;
  for (const [canonical, legacy] of Object.entries(LEGACY_DATA_KEY_ALIASES)) {
    if (canonical in out && !(legacy in out)) {
      out = { ...out, [legacy]: out[canonical] };
    }
  }
  return out;
}

/**
 * Expand one canonical event into the wire events to emit during the
 * migration window: the canonical event (with legacy payload keys
 * dual-written into `data`) first, then — for the renamed widget lifecycle
 * events — a mirror under the retired `*_card` name with the same payload
 * plus the `deprecated_alias: true` marker.
 */
export function expandLegacyAliases(event: TrackEvent): Array<TrackEvent | LegacyAliasEvent> {
  // An event with no `data` keeps the key absent — an explicit `undefined`
  // reads as a payload on the wire.
  const canonical: TrackEvent =
    event.data === undefined ? event : { ...event, data: withLegacyDataKeys(event.data) };
  const out: Array<TrackEvent | LegacyAliasEvent> = [canonical];
  if (canonical.context === AnalyticsContext.WidgetView) {
    const legacyName = (LEGACY_EVENT_ALIASES as Partial<Record<string, LegacyEventAlias>>)[
      canonical.event
    ];
    if (legacyName) {
      out.push({
        ...canonical,
        event: legacyName,
        data: { ...(canonical.data ?? {}), [LEGACY_ALIAS_MARKER]: true },
      });
    }
  }
  return out;
}

/**
 * Curated labels for navigation/screen_view events. Deliberately hand-named
 * (not derived from route paths) so labels stay stable across route refactors
 * and never leak entity ids.
 *
 * A name outlives the route that produced it: every screen_view already
 * recorded carries its label as a wire string, so a name is how that history
 * is still addressable after the screen is gone. Retire a screen by dropping
 * its entry from the app's `SCREEN_BY_ROUTE` map, never by deleting the
 * constant, and mark the constant `@deprecated`.
 */
export const AnalyticsScreenName = {
  AccountFollowUps: "account_follow_ups",
  Accounts: "accounts",
  Contacts: "contacts",
  /** @deprecated the workspace root is a redirect now; kept because stored rows carry the name */
  Dashboard: "dashboard",
  Dashboards: "dashboards",
  DashboardsMine: "dashboards_mine",
  FollowUps: "follow_ups",
  Inbox: "inbox",
  Map: "map",
  MemberDetail: "member_detail",
  Members: "members",
  MyWork: "my_work",
  OrgChart: "org_chart",
  SharedWithMe: "shared_with_me",
  Teams: "teams",
  TeamBoard: "team_board",
  TeamChart: "team_chart",
  TeamList: "team_list",
  /** @deprecated the route is gone; kept because stored rows carry the name */
  TeamDashboard: "team_dashboard",
  TeamOverview: "team_overview",
  TeamMembers: "team_members",
  Workflows: "workflows",
  WorkflowBoard: "workflow_board",
  WorkflowList: "workflow_list",
  WorkflowDashboard: "workflow_dashboard",
  // A saved custom view of any type (board, list or dashboard).
  WorkflowView: "workflow_view",
  WorkspaceDashboard: "workspace_dashboard",
  SettingsAccess: "settings_access",
  SettingsAccessKeyCreate: "settings_access_key_create",
  SettingsAccessKeyDetail: "settings_access_key_detail",
  SettingsAccessKeyEdit: "settings_access_key_edit",
  SettingsAccountTypeDetail: "settings_account_type_detail",
  SettingsAccountTypes: "settings_account_types",
  SettingsApi: "settings_api",
  SettingsApiKeyCreate: "settings_api_key_create",
  SettingsApiKeyDetail: "settings_api_key_detail",
  SettingsApiKeyEdit: "settings_api_key_edit",
  SettingsAppearance: "settings_appearance",
  SettingsContactTypeDetail: "settings_contact_type_detail",
  SettingsContactTypes: "settings_contact_types",
  /** @deprecated the route is gone; kept because stored rows carry the name */
  SettingsCreateTeam: "settings_create_team",
  SettingsCustomFields: "settings_custom_fields",
  SettingsIssueSources: "settings_issue_sources",
  SettingsIssueTypeDetail: "settings_issue_type_detail",
  SettingsIssueTypes: "settings_issue_types",
  SettingsMemberDetail: "settings_member_detail",
  SettingsMembers: "settings_members",
  SettingsOutcomes: "settings_outcomes",
  SettingsPermissions: "settings_permissions",
  SettingsPreferences: "settings_preferences",
  SettingsProfile: "settings_profile",
  /** @deprecated Outcomes absorbed the page; kept because stored rows carry the name */
  SettingsReasons: "settings_reasons",
  /** @deprecated the stages dialog replaced the page; kept because stored rows carry the name */
  SettingsStageDetail: "settings_stage_detail",
  SettingsTeamDetail: "settings_team_detail",
  /** @deprecated the route is gone; kept because stored rows carry the name */
  SettingsTeamMembers: "settings_team_members",
  SettingsTeams: "settings_teams",
  SettingsTrash: "settings_trash",
  /** @deprecated Outcomes absorbed the page; kept because stored rows carry the name */
  SettingsUpdates: "settings_updates",
  SettingsWebhookCreate: "settings_webhook_create",
  SettingsWebhookDetail: "settings_webhook_detail",
  SettingsWebhookEdit: "settings_webhook_edit",
  SettingsWorkflowDetail: "settings_workflow_detail",
  /** @deprecated the stages dialog replaced the page; kept because stored rows carry the name */
  SettingsWorkflowStages: "settings_workflow_stages",
  SettingsWorkspace: "settings_workspace",
  SettingsWorkflows: "settings_workflows",
} as const;
export type AnalyticsScreenName = (typeof AnalyticsScreenName)[keyof typeof AnalyticsScreenName];
